<script setup>
import { defineProps, toRefs, inject, computed } from 'vue';
import { GROUP_TYPE } from 'src/utils/config';
import { useIns } from 'src/utils/v2_helper/common';
import { formatDate } from 'src/utils/base';
import { useRoute } from 'src/utils/v2_helper/router';
import PkgProductItem from '../../result/components/PkgProductItem.vue';
import ProductItem from './ProductItem.vue';
import { genGroups, isPkg } from '../util';

const app = useIns();
const route = useRoute();

const orderData = inject('orderData');

const detail = computed(() => orderData.value?.orderDetail || {});
const orderGroups = computed(() => detail.value?.orderGroups);
// 优惠券
const promo = computed(() => {
  if (route.name === 'OnGoing') return null;
  return detail.value?.promos && detail.value.promos[0];
});
// 服务费
const serviceFee = computed(() => {
  const { serviceCharge } = detail.value || {};
  const { rate, amount } = serviceCharge || {};
  if (!amount && !rate) return null;
  return { amount, rate };
});

// 运费
const sheppingFee = computed(() => detail.value?.shippingTotal);
// 税
const taxFee = computed(() => {
  const { tax } = detail.value || {};
  const { rate, amount, inclusive } = tax || {};
  if (!rate && !amount) return null;
  return { rate, amount, inclusive };
});
// 时间
const createdTime = computed(
  () => detail.value?.createdTime?.dbStamp || Date.now(),
);
// 备注comment
const comment = computed(() => detail.value?.comment);

// free product
const freeProducts = computed(() => {
  if (route.name === 'OnGoing') return [];
  return detail.value?.orderGiftItems || [];
});

const hideTimeline = inject('hide_timeline', false);
</script>
<template>
  <div class="rec">
    <section class="rec-tile">
      <div class="tile" bold>{{ $t('result.receipt') }}</div>
      <div class="time">
        {{ formatDate(createdTime, 'yyyy.MM.dd HH:mm:ss') }}
      </div>
    </section>
    <section class="rec-list">
      <div
        v-for="(group, i) in orderGroups"
        :key="group.orderGroupId"
        class="rec-list-group"
      >
        <div v-if="group.orderGroupId && !hideTimeline" class="group-tile">
          <span>#Order{{ orderGroups.length - i }}</span>
          <div class="line" />
          <span>{{ formatDate(group.statusDateTime, 'HH:mm') }}</span>
        </div>
        <ProductItem
          v-for="prod in group.orderItems"
          :key="prod.id"
          :prod="prod"
        >
          <template #pkg>
            <PkgProductItem :product="prod" />
          </template>
        </ProductItem>
      </div>
      <!--Free Product-->
      <div>
        <ProductItem
          v-for="prod in freeProducts"
          :key="prod.id"
          :prod="prod"
          free
        >
          <template #pkg>
            <PkgProductItem :product="prod" />
          </template>
        </ProductItem>
      </div>
    </section>
    <section v-if="comment" class="rec-note">
      <div class="rec-note-k">{{ $t('fullDinein.note') }}</div>
      <div class="rec-note-v" break>{{ comment }}</div>
    </section>
    <section class="rec-fee">
      <div v-if="promo" class="fee-item promo">
        <span class="fee-item-k">{{ promo.name }}</span>
        <span class="fee-item-v">{{
          $n(promo.amount, 'currency', 'id-ID')
        }}</span>
      </div>
      <div v-if="!!serviceFee" class="fee-item">
        <span class="fee-item-k">
          {{ $t('checkout.serviceCharge') }}
          <span v-if="serviceFee.rate">{{ serviceFee.rate }}%</span>
        </span>
        <span class="fee-item-v">{{
          $n(serviceFee.amount, 'currency', 'id-ID')
        }}</span>
      </div>
      <div v-if="taxFee && !taxFee.inclusive" class="fee-item">
        <span class="fee-item-k">
          <span>
            {{ $t('checkout.taxExclusive') }}
            <span v-if="taxFee.rate">{{ taxFee.rate }}%</span>
          </span>
        </span>
        <span class="fee-item-v">{{
          $n(taxFee.amount, 'currency', 'id-ID')
        }}</span>
      </div>
      <div v-if="sheppingFee" class="fee-item">
        <span class="fee-item-k">{{ $t('result.freight') }}</span>
        <span class="fee-item-v">{{
          $n(sheppingFee, 'currency', 'id-ID')
        }}</span>
      </div>
      <div v-if="detail.rounding" class="fee-item">
        <span class="fee-item-k">{{ $t('checkout.rounding') }}</span>
        <span class="fee-item-v">{{
          $n(detail.rounding, 'currency', 'id-ID')
        }}</span>
      </div>
      <div class="fee-item total">
        <span class="fee-item-k">{{ $t('result.total') }}</span>
        <span class="fee-item-v" bold>
          <span class="normal">
            {{ $n(detail.grandTotal || 0, 'currency', 'id-ID') }}
          </span>
          <span v-if="detail.grandTotal !== detail.listTotal" class="del">{{
            $n(detail.listTotal || 0, 'currency', 'id-ID')
          }}</span>
        </span>
      </div>
      <p v-if="taxFee && taxFee.inclusive" class="inclusive">
        {{ $t('checkout.taxInclusiv') }}
        <b>{{ taxFee.rate }}%</b>
        {{ $n(taxFee.amount, 'currency', 'id-ID') }}
      </p>
    </section>
  </div>
</template>

<style scoped lang="scss">
.rec {
  margin: 24px;
  margin-top: 0;
  padding: 32px;
  background: #f3f4f6;
  border-radius: 8px;
  section + section {
    border-top: solid 1px #e5e7eb;
    padding-top: 16px;
  }
  &-tile {
    padding-bottom: 16px;
    .tile {
      font-size: 16px;
    }
    .time {
      color: #6b7280;
    }
  }
  &-fee {
    font-size: 12px;
    .fee-item {
      display: flex;
      & + .fee-item {
        padding-top: 20px;
      }
      &-v {
        margin-left: auto;
      }
      &.promo {
        color: $primary;
      }
      &.total {
        border-top: solid 1px #e5e7eb;
        margin-top: 16px;
        .fee-item-v {
          font-size: 16px;
          display: flex;
          flex-direction: column;
        }
        .del {
          text-decoration: line-through;
          font-size: $font-size-1;
          color: $grayFont;
          align-self: flex-end;
          line-height: 0.8;
          font-weight: normal;
        }
      }
    }
  }
  &-list {
    &-group {
      .group-tile {
        font-size: 12px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 8px;
        color: #9ca3af;
      }
      .line {
        margin: 0 8px;
        border-top: solid 1px #e5e7eb;
        flex: 1;
      }
    }
  }
  &-note {
    display: flex;
    padding: 16px 0;
    border-top: solid 1px #e5e7eb;
    font-size: 12px;
    &-k {
      font-weight: bold;
    }
    &-v {
      padding-left: 25px;
    }
  }
  .inclusive {
    color: #6b7280;
    font-size: 12px;
    text-align: right;
  }
}
</style>
